import { useEffect, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppSelector } from 'redux/hooks';

import { CategoryRequestBody, categoryDataService } from 'services/category.service';

import { useFetch } from 'hooks';

import { mapSeasons } from 'helpers/mapSeasons';

import { routes } from 'data/routes';

import { useUsers } from 'pages/Users/Users.hooks';

import { EditForm, FormItem } from 'sections/EditForm';
import { EditList } from 'sections/EditList';
import { SectionType } from 'sections/TabWrap';

import { Button } from 'components/Button';

const useData = () => {
  useUsers();
  const { id } = useParams();
  const navigate = useNavigate();
  const seasonsData = useAppSelector((state) => state.constants.seasons);

  const marketingLineOptions = useMemo(() => {
    const seasonsToObject = mapSeasons(seasonsData);
    const allLines = seasonsToObject.flatMap((s) => s.catalog);
    const uniqueLines = Array.from(new Set(allLines));
    return uniqueLines.map((line) => ({ value: line, label: line }));
  }, [seasonsData]);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { isDirty, isValid, isSubmitting },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      seasonLine: '',
      nameIt: '',
      nameEn: '',
      subCategories: [] as Array<{ nameIt: string; nameEn: string }>,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'subCategories',
  });

  const { data: categoryData } = useFetch(
    id ? () => categoryDataService.getById(id) : undefined,
    [id]
  );

  useEffect(() => {
    if (categoryData) {
      setValue('seasonLine', categoryData.seasonLine);
      setValue('nameIt', categoryData.nameIt);
      setValue('nameEn', categoryData.nameEn);
      if (categoryData.subCategories?.length) {
        setValue(
          'subCategories',
          categoryData.subCategories.map((sub: { nameIt: string; nameEn: string }) => ({
            nameIt: sub.nameIt,
            nameEn: sub.nameEn,
          }))
        );
      }
    }
  }, [categoryData, setValue]);

  const onSubmit = (formValues: Record<string, any>) => {
    const body: CategoryRequestBody = {
      categoryDto: {
        ...(id ? { id } : {}),
        seasonLine: formValues.seasonLine,
        nameIt: formValues.nameIt,
        nameEn: formValues.nameEn,
        subCategories: formValues.subCategories,
      },
      subCategoryDtos: formValues.subCategories,
    };

    const serviceCall = id
      ? categoryDataService.edit(id, body)
      : categoryDataService.create(body);

    serviceCall
      .then(() => {
        toast.success(id ? 'Category edited successfully!' : 'Category created successfully!');
        navigate(`${routes.catalogue}/notifications-categories`);
      })
      .catch((err) => {
        toast.error(err?.message || 'Failed to save category');
      });
  };

  const data: FormItem[] = [
    {
      name: 'seasonLine',
      value: categoryData?.seasonLine ?? '',
      label: 'Season Line',
      type: 'select',
      options: marketingLineOptions,
      required: true,
    },
    {
      name: 'nameEn',
      value: categoryData?.nameEn ?? '',
      label: 'Category name EN',
      flag: 'en',
      required: true,
    },
    {
      name: 'nameIt',
      value: categoryData?.nameIt ?? '',
      label: 'Category name IT',
      flag: 'it',
      required: true,
    },
  ];

  const subCategoriesMapped = fields.map((field, index) => ({
    id: index,
    name_it: field.nameIt,
    name_en: field.nameEn,
  }));

  const sections: SectionType[] = [
    {
      id: 1,
      content: (
        <EditForm
          onSubmit={handleSubmit(onSubmit)}
          data={data}
          register={register as any}
          control={control as any}
          hasButtons
          hideUndo
          submitBtnLabel={id ? 'Save' : 'Submit'}
          isDirty={isDirty}
          isButtonsDisabled={!isDirty || !isValid || isSubmitting}
          buttonsExtra={
            <Button
              type="button"
              motive="primaryBlack"
              fontFamily="LFT Zeno Condensed"
              textTransform="uppercase"
              scale="base"
              onClick={() => append({ nameIt: '', nameEn: '' })}
            >
              Add SubCategory
            </Button>
          }
        />
      ),
    },
    {
      id: 2,
      content: (
        <EditList
          subCategories={subCategoriesMapped}
          register={register as any}
          onRemove={remove}
        />
      ),
    },
  ];

  return {
    sections,
    fields,
    append,
    remove,
  };
};

export { useData };
